// src/types.ts
var SimulationError = class extends Error {
  constructor(code, message) {
    super(message);
    this.code = code;
    this.name = "SimulationError";
  }
};

// src/helpers.ts
function avg(players, value) {
  if (players.length === 0) return 10;
  let sum = 0;
  for (const p of players) sum += value(p);
  return sum / players.length;
}
function mean(values) {
  if (values.length === 0) return 0;
  let sum = 0;
  for (const v of values) sum += v;
  return sum / values.length;
}
function round2(value) {
  return Math.round(value * 100) / 100;
}

// src/synthetic.ts
function tuned(value, isKey, delta) {
  if (isKey) return Math.min(20, value + delta);
  return Math.max(1, value - 1);
}
function defaultRole(position) {
  const map = {
    GK: "sweeperKeeper",
    CB: "ballPlayingDefender",
    RB: "fullBack",
    LB: "fullBack",
    DM: "anchor",
    CM: "boxToBoxMidfielder",
    AM: "advancedPlaymaker",
    RW: "insideForward",
    LW: "insideForward",
    ST: "advancedForward"
  };
  return map[position] || "default";
}
function defaultDuty(position) {
  if (position === "GK" || position === "CB" || position === "DM") return "defend";
  if (position === "ST") return "attack";
  return "support";
}
function primaryPosition(player) {
  let best = "";
  let bestRating = 0;
  for (const pos of player.positions) {
    if (pos.rating > bestRating) {
      bestRating = pos.rating;
      best = pos.position;
    }
  }
  return best || "CM";
}
function isForward(position) {
  return position === "ST" || position === "CF";
}
function isWideOrForward(position) {
  return ["ST", "CF", "LW", "RW", "W", "AM"].includes(position);
}
function isMidfielder(position) {
  return ["DM", "CM", "AM"].includes(position);
}
function positionGroup(position) {
  if (position === "GK") return "goalkeeper";
  if (["CB", "LB", "RB", "LWB", "RWB", "FB"].includes(position)) return "defender";
  if (["DM", "CM", "AM"].includes(position)) return "midfielder";
  if (["LW", "RW", "W", "ST", "CF"].includes(position)) return "forward";
  return "midfielder";
}
function syntheticPlayer(teamId, teamName, idx, position, label, ca) {
  const level = Math.max(4, Math.min(18, Math.round((ca - 95) / 6)));
  const isGk = position === "GK";
  const defender = ["RB", "CB", "LB", "DM"].includes(position);
  const attacker = ["AM", "RW", "LW", "ST"].includes(position);
  const wide = ["RB", "LB", "RW", "LW"].includes(position);
  const striker = position === "ST";
  return {
    id: `${teamId}_p${String(idx + 1).padStart(2, "0")}`,
    name: `${teamName} ${label}`,
    age: 21 + (idx * 3 + teamId.length) % 14,
    positions: [{ position, rating: 20 }],
    roleFit: [{ role: defaultRole(position), duty: defaultDuty(position), fit: 16 }],
    preferredFoot: idx % 6 === 0 ? "left" : "right",
    heightCm: position === "GK" ? 190 + idx % 5 : ["CB", "ST"].includes(position) ? 184 + idx % 8 : 172 + idx % 10,
    weightKg: 70 + idx % 17,
    status: {
      condition: 88 + idx % 9,
      matchSharpness: 78 + idx % 13,
      morale: 72 + idx % 18,
      injuryRisk: 4 + idx % 9,
      suspensionRisk: idx % 3
    },
    technical: {
      finishing: tuned(level, attacker, 2),
      longShots: tuned(level, attacker, 1),
      passing: tuned(level, !isGk, 1),
      crossing: tuned(level, wide, 2),
      dribbling: tuned(level, attacker, 2),
      firstTouch: tuned(level, !isGk, 1),
      heading: tuned(level, ["CB", "ST"].includes(position), 2),
      tackling: tuned(level, defender, 2),
      marking: tuned(level, defender, 2),
      freeKicks: Math.max(1, level - 2),
      corners: Math.max(1, level - 2),
      penalties: tuned(level, striker, 2)
    },
    mental: {
      decisions: level,
      anticipation: tuned(level, defender || attacker, 1),
      offTheBall: tuned(level, attacker, 2),
      vision: tuned(level, ["CM", "AM"].includes(position), 2),
      teamwork: level,
      workRate: tuned(level, !isGk, 1),
      composure: level,
      bravery: tuned(level, defender, 1),
      aggression: 9 + idx % 6,
      concentration: tuned(level, defender || isGk, 2),
      leadership: Math.max(1, level - 2),
      bigMatches: Math.max(1, level - 1),
      consistency: level
    },
    physical: {
      pace: tuned(level, wide || striker, 2),
      acceleration: tuned(level, ["RW", "LW"].includes(position), 2),
      stamina: tuned(level, !isGk, 1),
      strength: tuned(level, ["CB", "DM", "ST"].includes(position), 2),
      jumpingReach: tuned(level, ["GK", "CB", "ST"].includes(position), 2),
      agility: level,
      balance: level,
      naturalFitness: level
    },
    goalkeeping: {
      shotStopping: isGk ? tuned(level, true, 3) : 1,
      reflexes: isGk ? tuned(level, true, 3) : 1,
      oneOnOnes: isGk ? tuned(level, true, 2) : 1,
      aerialReach: isGk ? tuned(level, true, 2) : 1,
      handling: isGk ? tuned(level, true, 2) : 1,
      distribution: isGk ? level : 1,
      rushingOut: isGk ? level : 1,
      commandOfArea: isGk ? tuned(level, true, 1) : 1
    },
    hidden: {
      currentAbility: ca,
      potentialAbility: Math.min(200, ca + 8),
      formVolatility: 8 + idx % 8,
      pressure: level,
      professionalism: tuned(level, true, 1),
      adaptability: level,
      injuryProneness: 5 + idx % 9
    }
  };
}
function inflatePlayer(simple, teamId, teamName, idx) {
  const base = syntheticPlayer(teamId, teamName, idx, simple.position, simple.name, simple.currentAbility);
  base.id = simple.id;
  base.name = simple.name;
  base.status.condition = Math.round(simple.stamina);
  base.status.injuryRisk = simple.injured ? 80 : Math.max(1, base.status.injuryRisk - 10);
  base.hidden.currentAbility = simple.currentAbility;
  return base;
}
function inflateTeam(team) {
  const avgCa = team.players.reduce((s, p) => s + p.currentAbility, 0) / Math.max(team.players.length, 1);
  const rep = Math.max(1, Math.min(20, Math.round((avgCa - 85) / 5)));
  const coach = team.coach || {
    name: "Default Coach",
    tacticalKnowledge: rep,
    inGameAdjustments: rep,
    attackingCoaching: rep,
    defensiveCoaching: rep,
    trainingQuality: rep,
    disciplineManagement: rep,
    rotationTendency: rep,
    youthUsage: rep,
    riskAppetite: Math.round(rep / 2),
    substitutionTiming: Math.round(rep / 2)
  };
  const profile = team.profile || {
    reputation: rep,
    homeAdvantage: 10,
    squadDepth: Math.max(6, rep - 2),
    cohesion: Math.min(100, rep * 5),
    morale: Math.min(100, rep * 4 + 15),
    fatigue: 10,
    injuryPressure: 8,
    discipline: Math.min(100, rep * 5),
    setPieceQuality: Math.min(100, rep * 5),
    benchImpact: Math.max(5, rep - 3)
  };
  const players = team.players.map((p, i) => inflatePlayer(p, team.id, team.name, i));
  const startingXi = team.startingXi.length === 11 ? team.startingXi : preferredXi(players);
  const bench = team.bench.length > 0 ? team.bench : players.map((p) => p.id).filter((id) => !startingXi.includes(id));
  return { coach, profile, players, startingXi, bench };
}
function preferredXi(players) {
  if (players.length < 11) return players.map((p) => p.id);
  const gk = players.filter((p) => primaryPosition(p) === "GK").sort((a, b) => b.hidden.currentAbility - a.hidden.currentAbility);
  const def = players.filter((p) => ["RB", "CB", "LB"].includes(primaryPosition(p))).sort((a, b) => b.hidden.currentAbility - a.hidden.currentAbility);
  const mid = players.filter((p) => ["DM", "CM", "AM"].includes(primaryPosition(p))).sort((a, b) => b.hidden.currentAbility - a.hidden.currentAbility);
  const fwd = players.filter((p) => ["RW", "LW", "ST"].includes(primaryPosition(p))).sort((a, b) => b.hidden.currentAbility - a.hidden.currentAbility);
  const xi = [];
  if (gk.length > 0) xi.push(gk[0].id);
  for (let i = 0; i < Math.min(4, def.length); i++) xi.push(def[i].id);
  for (let i = 0; i < Math.min(3, mid.length); i++) xi.push(mid[i].id);
  for (let i = 0; i < Math.min(3, fwd.length); i++) xi.push(fwd[i].id);
  const remaining = players.filter((p) => !xi.includes(p.id)).sort((a, b) => b.hidden.currentAbility - a.hidden.currentAbility);
  for (const p of remaining) {
    if (xi.length >= 11) break;
    xi.push(p.id);
  }
  return xi;
}

// src/formation.ts
function formationShape(formation) {
  const digits = formation.split(/[^0-9]/).map(Number).filter((n) => !isNaN(n) && n > 0);
  if (digits.length >= 3) {
    const defenders = digits[0];
    const midfielders = digits.slice(1, -1).reduce((a, b) => a + b, 0);
    const forwards = digits[digits.length - 1];
    return { defenders, midfielders, forwards };
  }
  return { defenders: 4, midfielders: 3, forwards: 3 };
}
function formationSlots(formation) {
  if (formation.includes("5-3-2")) return slots(["GK", "RWB", "CB", "CB", "CB", "LWB", "DM", "CM", "CM", "ST", "ST"]);
  if (formation.includes("4-2-3-1")) return slots(["GK", "RB", "CB", "CB", "LB", "DM", "CM", "AM", "LW", "RW", "ST"]);
  if (formation.includes("4-3-3") && formation.includes("DM")) return slots(["GK", "RB", "CB", "CB", "LB", "DM", "CM", "AM", "RW", "ST", "LW"]);
  if (formation.includes("4-3-3")) return slots(["GK", "RB", "CB", "CB", "LB", "CM", "CM", "AM", "RW", "ST", "LW"]);
  if (formation.includes("4-4-2")) return slots(["GK", "RB", "CB", "CB", "LB", "LM", "CM", "CM", "RM", "ST", "ST"]);
  if (formation.includes("3-4-2-1")) return slots(["GK", "CB", "CB", "CB", "LWB", "CM", "CM", "RWB", "LW", "RW", "ST"]);
  if (formation.includes("3-2-5")) return slots(["GK", "CB", "CB", "CB", "DM", "DM", "LW", "AM", "ST", "AM", "RW"]);
  if (formation.includes("2-3-5")) return slots(["GK", "CB", "CB", "RB", "DM", "LB", "RW", "AM", "ST", "AM", "LW"]);
  return slots(["GK", "RB", "CB", "CB", "LB", "DM", "CM", "AM", "RW", "ST", "LW"]);
}
function slots(values) {
  return [...values];
}
function samePositionGroup(a, b) {
  return positionGroup(a) === positionGroup(b);
}
function adjacentPosition(a, b) {
  const pairs = [
    ["LB", "LWB"],
    ["LWB", "LB"],
    ["RB", "RWB"],
    ["RWB", "RB"],
    ["DM", "CM"],
    ["CM", "DM"],
    ["CM", "AM"],
    ["AM", "CM"],
    ["LW", "RW"],
    ["RW", "LW"],
    ["ST", "CF"],
    ["CF", "ST"]
  ];
  return pairs.some(([x, y]) => x === a && y === b);
}
function positionFit(player, slot) {
  let best = 0;
  for (const position of player.positions) {
    let rating;
    if (position.position === slot) {
      rating = position.rating;
    } else if (samePositionGroup(position.position, slot)) {
      rating = position.rating * 0.72;
    } else if (adjacentPosition(position.position, slot)) {
      rating = position.rating * 0.52;
    } else {
      rating = position.rating * 0.22;
    }
    if (rating > best) best = rating;
  }
  return Math.max(1, Math.min(20, best));
}
function isWidePosition(position) {
  return ["LB", "RB", "LWB", "RWB", "LW", "RW", "W"].includes(position);
}

// src/tactics.ts
function buildTeamContext(teamId, teamName, tactic, profile, coach, players, startingXi, bench, isHome) {
  const playerMap = new Map(players.map((p) => [p.id, p]));
  const starters = startingXi.map((id) => playerMap.get(id)).filter(Boolean);
  const benchPlayers = bench.map((id) => playerMap.get(id)).filter(Boolean);
  const goalkeeper = starters.find((p) => primaryPosition(p) === "GK") || players.find((p) => primaryPosition(p) === "GK");
  const outfield = starters.filter((p) => primaryPosition(p) !== "GK");
  const slots2 = formationSlots(tactic.base.formation);
  const instructions = new Map(
    tactic.playerInstructions.map((inst) => [inst.playerId, inst])
  );
  const tactProfile = computeTacticalProfile(tactic, starters, slots2, instructions);
  const playerReadiness = avg2(
    starters,
    (p) => p.status.condition * 0.45 + p.status.matchSharpness * 0.3 + p.status.morale * 0.2 - p.status.injuryRisk * 0.15
  );
  const teamReadiness = playerReadiness / 100 - profile.fatigue * 6e-3 - profile.injuryPressure * 4e-3 + (profile.morale - 75) * 2e-3;
  const readinessBonus = (teamReadiness - 0.75) * 8;
  const positionBonus = (tactProfile.positionFit - 0.72) * 10;
  const roleBonus = (tactProfile.roleFit - 0.7) * 7;
  const tacticBonus = (tactProfile.tacticFit - 12) * 0.45;
  const attack = avg2(
    outfield,
    (p) => mean([p.technical.finishing, p.technical.passing, p.technical.dribbling, p.mental.offTheBall, p.mental.vision, p.physical.pace])
  ) + coach.attackingCoaching * 0.35 + profile.cohesion * 0.15 + readinessBonus + positionBonus + roleBonus * 0.6 + tacticBonus + tactProfile.forwards * 0.22 + tactProfile.widthSupply * 0.08;
  const defence = avg2(
    starters,
    (p) => mean([p.technical.tackling, p.technical.marking, p.mental.anticipation, p.mental.concentration, p.physical.strength])
  ) + coach.defensiveCoaching * 0.35 + goalkeeperScore(goalkeeper) * 0.18 + readinessBonus * 0.8 + positionBonus * 0.85 + roleBonus * 0.45 + tacticBonus * 0.65 + tactProfile.defenders * 0.28 + tactProfile.centralDensity * 0.08;
  const control = avg2(
    outfield,
    (p) => mean([p.technical.passing, p.technical.firstTouch, p.mental.decisions, p.mental.teamwork, p.mental.composure])
  ) + tactic.inPossession.shortPassing * 0.9 + tactic.base.tempo * 0.25 + readinessBonus * 0.55 + positionBonus * 0.9 + roleBonus * 0.7 + tacticBonus + tactProfile.midfielders * 0.22;
  const discipline = (profile.discipline + coach.disciplineManagement + avg2(starters, (p) => 21 - p.mental.aggression)) / 3;
  const shotQuality = avg2(
    outfield,
    (p) => mean([p.technical.finishing, p.mental.composure, p.mental.decisions, p.technical.firstTouch])
  ) + readinessBonus * 0.4 + positionBonus * 0.4 + roleBonus * 0.35 + tacticBonus * 0.55;
  return {
    team: { id: teamId, name: teamName, tactic, profile, ...profile },
    coach,
    isHome,
    outfield,
    starters,
    bench: benchPlayers,
    goalkeeper,
    slots: slots2,
    instructions,
    attack,
    defence,
    control,
    discipline,
    shotQuality,
    tacticalProfile: tactProfile,
    teamId
  };
}
function computeTacticalProfile(tactic, starters, slots2, instructions) {
  const formation = formationShape(tactic.base.formation);
  const posFit = avg2(starters, (player, _idx, arr) => {
    const idx = arr.indexOf(player);
    const slot = slots2[idx] || primaryPosition(player);
    return positionFit(player, slot);
  }, starters);
  const roleFit = avg2(starters, (player) => {
    const inst = instructions.get(player.id);
    if (inst) {
      const role = player.roleFit.find((r) => r.role === inst.role);
      const roleScore = role ? role.fit : naturalRoleFit(player) * 0.8;
      const dutyScore = instructionDutyFit(player, inst.duty);
      return Math.max(1, Math.min(20, roleScore * 0.65 + dutyScore * 0.35));
    }
    return naturalRoleFit(player);
  }, starters);
  const tacFit = avg2(starters, (player) => playerTacticFit(player, tactic), starters);
  const widthSupply = avg2(starters, (p) => {
    const pos = primaryPosition(p);
    if (["LB", "RB", "LWB", "RWB", "LW", "RW", "W"].includes(pos)) {
      return p.technical.crossing + p.physical.stamina * 0.4;
    }
    return 5;
  }, starters);
  return {
    positionFit: posFit / 20,
    roleFit: roleFit / 20,
    tacticFit: tacFit,
    defenders: formation.defenders,
    midfielders: formation.midfielders,
    forwards: formation.forwards,
    widthSupply,
    centralDensity: formation.midfielders * 3 + formation.defenders * 1.5
  };
}
function naturalRoleFit(player) {
  let best = 12;
  for (const fit of player.roleFit) {
    if (fit.fit > best) best = fit.fit;
  }
  return best;
}
function instructionDutyFit(player, duty) {
  switch (duty) {
    case "attack":
      return mean([player.technical.finishing, player.mental.offTheBall, player.physical.pace]);
    case "support":
      return mean([player.technical.passing, player.mental.teamwork, player.mental.decisions]);
    case "defend":
      return mean([player.technical.tackling, player.technical.marking, player.mental.concentration]);
  }
}
function playerTacticFit(player, tactic) {
  const possessionFit = mean([player.technical.passing, player.technical.firstTouch, player.mental.decisions, player.mental.teamwork]);
  const directFit = mean([player.technical.crossing, player.technical.heading, player.physical.strength, player.physical.pace]);
  const pressingFit = mean([player.physical.stamina, player.mental.workRate, player.mental.aggression, player.physical.acceleration]);
  const defensiveFit = mean([player.technical.tackling, player.technical.marking, player.mental.anticipation, player.mental.concentration]);
  let score = possessionFit * tactic.inPossession.shortPassing + directFit * tactic.inPossession.passingDirectness + pressingFit * tactic.outOfPossession.pressingIntensity * 2 + defensiveFit * tactic.outOfPossession.compactness * 2;
  const weight = tactic.inPossession.shortPassing + tactic.inPossession.passingDirectness + tactic.outOfPossession.pressingIntensity * 2 + tactic.outOfPossession.compactness * 2;
  score /= Math.max(weight, 1);
  return Math.max(1, Math.min(20, score));
}
function assignedSlotBonus(slot, action) {
  switch (action) {
    case "shoot":
    case "xg":
      if (["ST", "CF", "LW", "RW", "AM"].includes(slot)) return 5;
      return 0;
    case "assist":
    case "passing":
      if (["DM", "CM", "AM"].includes(slot)) return 5;
      return 0;
    case "carry":
      if (["LW", "RW", "LWB", "RWB", "LB", "RB"].includes(slot)) return 4;
      return 0;
    default:
      return 0;
  }
}
function goalkeeperScore(goalkeeper) {
  return mean([
    goalkeeper.goalkeeping.shotStopping,
    goalkeeper.goalkeeping.reflexes,
    goalkeeper.goalkeeping.oneOnOnes,
    goalkeeper.goalkeeping.handling,
    goalkeeper.goalkeeping.commandOfArea
  ]);
}
function possessionExecutionScore(ctx) {
  return avg2(
    ctx.outfield,
    (p) => mean([p.technical.passing, p.technical.firstTouch, p.mental.decisions, p.mental.teamwork, p.mental.composure])
  );
}
function directExecutionScore(ctx) {
  return avg2(
    ctx.outfield,
    (p) => mean([p.technical.crossing, p.technical.heading, p.physical.strength, p.physical.pace])
  );
}
function possessionExecutionGap(ctx) {
  const intent = ctx.team.tactic.inPossession.shortPassing * 1 + ctx.team.tactic.base.timeManagement * 0.5;
  return Math.max(0, intent - possessionExecutionScore(ctx));
}
function dangerousTurnoverProbability(att, def) {
  const gap = possessionExecutionGap(att);
  const press = def.team.tactic.outOfPossession.pressingIntensity;
  const counterpress = def.team.tactic.transition.whenPossessionLost === "counterPress" ? 1 : 0;
  return Math.max(0, Math.min(
    0.035,
    1e-3 + gap * 35e-4 + press * 35e-4 + counterpress * 35e-4 - possessionExecutionScore(att) * 12e-4
  ));
}
function defensiveExposure(ctx) {
  const attackingCommitment = ctx.team.tactic.base.risk * 0.55 + ctx.team.tactic.inPossession.boxPresence * 0.35 + ctx.team.tactic.base.attackingWidth * 0.2 + ctx.team.tactic.inPossession.overlapLeft * 0.1 + ctx.team.tactic.inPossession.overlapRight * 0.1;
  const lineAndPress = ctx.team.tactic.outOfPossession.defensiveLine * 0.45 + ctx.team.tactic.outOfPossession.pressingIntensity * 0.3;
  const restDefence = ctx.tacticalProfile.defenders * 0.24 + ctx.tacticalProfile.centralDensity * 0.035 + ctx.team.tactic.outOfPossession.compactness * 0.4;
  return Math.max(0, Math.min(5, attackingCommitment + lineAndPress - restDefence));
}
function teamStrengthEdge(att, def) {
  const reputationEdge = (att.team.profile.reputation - def.team.profile.reputation) * 0.03;
  const coachEdge = (att.coach.attackingCoaching - def.coach.defensiveCoaching) * 0.035;
  const fitEdge = (att.tacticalProfile.positionFit - def.tacticalProfile.positionFit) * 1.4 + (att.tacticalProfile.roleFit - def.tacticalProfile.roleFit) * 1 + (att.tacticalProfile.tacticFit - def.tacticalProfile.tacticFit) * 0.045;
  const qualityEdge = (att.attack - def.defence) * 0.055 + (att.shotQuality - def.defence) * 0.03;
  return Math.max(-1.4, Math.min(1.4, reputationEdge + coachEdge + fitEdge + qualityEdge));
}
function extremeMismatchEdge(att, def) {
  const reputationGap = att.team.profile.reputation - def.team.profile.reputation;
  if (att.team.profile.reputation < 18 || reputationGap < 10 || def.team.profile.reputation > 8) return 0;
  const qualityGap = (att.attack - def.defence + (att.shotQuality - def.defence)) * 0.015;
  return Math.max(0, Math.min(0.5, (reputationGap - 8) * 0.06 + qualityGap));
}
function underdogCounterEdge(att, def) {
  const reputationDeficit = def.team.profile.reputation - att.team.profile.reputation;
  if (reputationDeficit >= 8 && def.team.profile.reputation < 18 && att.team.tactic.transition.whenPossessionWon === "counter") {
    return 2.3;
  }
  if (reputationDeficit < 2 || reputationDeficit > 8 || att.team.tactic.transition.whenPossessionWon !== "counter") return 0;
  const transition = att.team.tactic.transition.quickSwitches * 0.175 + att.team.tactic.inPossession.throughBalls * 0.125 + att.team.tactic.base.tempo * 0.09 + (att.tacticalProfile.tacticFit - 12) * 0.035;
  const opponentExposure = (def.team.tactic.outOfPossession.defensiveLine * 9.5 - 10) * 0.04 + (def.team.tactic.base.risk * 3.167 - 10) * 0.025;
  return Math.max(0, Math.min(0.9, transition + opponentExposure - 0.9));
}
function competitiveConversionEdge(att, def) {
  if (att.team.profile.reputation >= 16 && def.team.profile.reputation >= 16) return 0.45;
  return 0;
}
function favoriteConversionEdge(att, def) {
  if (att.team.profile.reputation >= 18 && def.team.profile.reputation >= 16 && att.team.profile.reputation > def.team.profile.reputation) return 0.65;
  return 0;
}
function possessionStyleFactor(ctx) {
  const tm = ctx.team.tactic.base.timeManagement;
  const short = ctx.team.tactic.inPossession.shortPassing;
  const direct = ctx.team.tactic.inPossession.passingDirectness;
  return Math.max(0.35, Math.min(1.1, 0.58 + tm * 0.12 + (short - direct) / 7));
}
function extremePossessionShift(home, away) {
  const homeGap = home.team.profile.reputation - away.team.profile.reputation;
  const awayGap = away.team.profile.reputation - home.team.profile.reputation;
  if (home.team.profile.reputation >= 18 && homeGap >= 10 && away.team.profile.reputation <= 8) {
    return Math.max(0, Math.min(0.12, (homeGap - 7) * 0.024));
  }
  if (away.team.profile.reputation >= 18 && awayGap >= 10 && home.team.profile.reputation <= 8) {
    return -Math.max(0, Math.min(0.12, (awayGap - 7) * 0.024));
  }
  return 0;
}
function underdogPossessionBias(favorite, underdog, gap) {
  if (gap < 6 || underdog.team.profile.reputation > 9) return 0;
  const controlGap = Math.max(0, favorite.control - underdog.control);
  const styleGap = Math.max(0, favorite.team.tactic.inPossession.shortPassing * 5 - underdog.team.tactic.inPossession.shortPassing * 5) + Math.max(0, underdog.team.tactic.outOfPossession.compactness * 9.5 - 12) * 0.45 + Math.max(0, 11 - underdog.team.tactic.outOfPossession.defensiveLine * 9.5) * 0.35;
  return Math.max(0, Math.min(0.04, (gap - 4) * 0.012 + controlGap * 35e-4 + styleGap * 4e-3));
}
function underdogPossessionShift(home, away) {
  const homeGap = home.team.profile.reputation - away.team.profile.reputation;
  const awayGap = away.team.profile.reputation - home.team.profile.reputation;
  const homeBias = underdogPossessionBias(home, away, homeGap);
  const awayBias = underdogPossessionBias(away, home, awayGap);
  return homeBias - awayBias;
}
function possessionProbability(home, away) {
  const rawHome = home.control + home.team.tactic.inPossession.shortPassing * 2.5 + home.tacticalProfile.midfielders * 0.45 + (home.tacticalProfile.positionFit - 0.72) * 12 - away.team.tactic.outOfPossession.pressingIntensity * 2.5 - possessionExecutionGap(home) * (0.8 + away.team.tactic.outOfPossession.pressingIntensity * 0.3) - dangerousTurnoverProbability(home, away) * 18;
  const rawAway = away.control + away.team.tactic.inPossession.shortPassing * 2.5 + away.tacticalProfile.midfielders * 0.45 + (away.tacticalProfile.positionFit - 0.72) * 12 - home.team.tactic.outOfPossession.pressingIntensity * 2 - possessionExecutionGap(away) * (0.8 + home.team.tactic.outOfPossession.pressingIntensity * 0.3) - dangerousTurnoverProbability(away, home) * 18;
  const skillHome = Math.max(0, Math.min(1, rawHome / Math.max(rawHome + rawAway, 0.01)));
  const homeFactor = possessionStyleFactor(home);
  const awayFactor = possessionStyleFactor(away);
  const tacticalHome = homeFactor / Math.max(homeFactor + awayFactor, 0.01);
  const homeBoost = home.team.profile.homeAdvantage * 4e-3;
  const combined = skillHome * 0.65 + tacticalHome * 0.35 + homeBoost;
  const normal = 0.5 + (combined - 0.5) * 0.8;
  const mismatchShift = extremePossessionShift(home, away);
  const underdogShift = underdogPossessionShift(home, away);
  return Math.max(0.15, Math.min(0.88, normal + mismatchShift + underdogShift));
}
function tacticalMatchupBonus(att, def) {
  let bonus = 0;
  const pressing = def.team.tactic.outOfPossession.pressingIntensity;
  const dLine = def.team.tactic.outOfPossession.defensiveLine;
  const attDirectness = att.team.tactic.inPossession.passingDirectness;
  const attShort = att.team.tactic.inPossession.shortPassing;
  const attCounter = att.team.tactic.transition.whenPossessionWon === "counter";
  const attThrough = att.team.tactic.inPossession.throughBalls;
  if (pressing >= 2 && attShort >= 3) bonus -= 0.022;
  if (pressing >= 2 && attDirectness >= 3) bonus += 0.03;
  if (dLine === 0 && attDirectness >= 3) bonus -= 0.025;
  if (dLine === 0 && attShort >= 3 && attThrough >= 3) bonus += 0.028;
  if (attCounter && dLine >= 2) bonus += 0.055;
  if (attCounter && dLine === 0) bonus -= 0.04;
  const widthMismatch = att.team.tactic.base.attackingWidth - def.team.tactic.base.defensiveWidth;
  if (widthMismatch > 1) bonus += 0.018;
  if (widthMismatch < -1) bonus -= 0.018;
  return bonus;
}
function tacticalMatchupEffect(att, def) {
  const bonus = tacticalMatchupBonus(att, def);
  const effect = {
    shotVolumeModifier: bonus * 0.8,
    xgModifier: bonus * 0.5,
    foulCardModifier: 0
  };
  if (def.team.tactic.outOfPossession.pressingIntensity >= 2 && att.team.tactic.inPossession.shortPassing >= 3) {
    const gap = possessionExecutionGap(att);
    effect.shotVolumeModifier -= gap * 4e-3;
    effect.xgModifier -= gap * 2e-3;
    effect.foulCardModifier += gap * 15e-4;
  }
  if (att.team.tactic.inPossession.passingDirectness >= 3 && def.team.tactic.outOfPossession.pressingIntensity >= 2) {
    effect.shotVolumeModifier += directExecutionScore(att) * 8e-4;
    effect.xgModifier += directExecutionScore(att) * 6e-4;
  }
  return effect;
}
function chooseLane(att, def, rng) {
  const leftEdge = laneMatchupEdge(att, def, "left");
  const centerEdge = laneMatchupEdge(att, def, "center");
  const rightEdge = laneMatchupEdge(att, def, "right");
  const focus = att.team.tactic.inPossession.attackFocus;
  const [focusLeft, focusCenter, focusRight] = focus === "left" ? [1.25, 0.95, 0.85] : focus === "center" ? [0.9, 1.25, 0.9] : focus === "right" ? [0.85, 0.95, 1.25] : [1, 1, 1];
  const width = att.team.tactic.base.attackingWidth;
  const weights = [
    Math.max(1, 12 + width * 1.5 + leftEdge * 1.2) * focusLeft,
    Math.max(1, 15 + centerEdge * 1.1) * focusCenter,
    Math.max(1, 12 + width * 1.5 + rightEdge * 1.2) * focusRight
  ];
  const total = weights[0] + weights[1] + weights[2];
  let r = rng.f64() * total;
  if (r < weights[0]) return "left";
  r -= weights[0];
  if (r < weights[1]) return "center";
  return "right";
}
function laneAttackers(ctx, lane) {
  return ctx.outfield.filter((p) => {
    const pos = primaryPosition(p);
    switch (lane) {
      case "left":
        return ["LW", "LWB", "LB", "W"].includes(pos);
      case "center":
        return ["ST", "CF", "AM", "CM", "DM"].includes(pos);
      case "right":
        return ["RW", "RWB", "RB", "W"].includes(pos);
    }
  });
}
function laneDefenders(ctx, lane) {
  return ctx.outfield.filter((p) => {
    const pos = primaryPosition(p);
    switch (lane) {
      case "left":
        return ["RB", "RWB", "CB", "DM"].includes(pos);
      case "center":
        return ["CB", "DM", "CM"].includes(pos);
      case "right":
        return ["LB", "LWB", "CB", "DM"].includes(pos);
    }
  });
}
function laneMatchupEdge(att, def, lane) {
  const attackers = laneAttackers(att, lane);
  const defenders = laneDefenders(def, lane);
  const attackPool = attackers.length > 0 ? attackers : att.outfield;
  const defencePool = defenders.length > 0 ? defenders : def.outfield;
  const attackScore = avg2(
    attackPool,
    (p) => mean([p.technical.dribbling, p.technical.crossing, p.technical.passing, p.mental.decisions, p.physical.pace])
  );
  const defenceScore = avg2(
    defencePool,
    (p) => mean([p.technical.tackling, p.technical.marking, p.mental.anticipation, p.mental.concentration, p.physical.pace])
  );
  return Math.max(-8, Math.min(8, attackScore - defenceScore));
}
function goalkeeperPassCount(att, def, passCount, rng) {
  const dist = att.team.tactic.transition.goalkeeperDistribution;
  const baseShare = dist === "short" || dist === "centerBacks" || dist === "fullBacks" ? 0.04 : dist === "mixed" ? 0.055 : 0.085;
  const styleBonus = (() => {
    const pd = att.team.tactic.inPossession.passingDirectness;
    const press = def.team.tactic.outOfPossession.pressingIntensity;
    const pod = att.team.tactic.inPossession.playOutOfDefence;
    const sp = att.team.tactic.inPossession.shortPassing;
    switch (dist) {
      case "short":
      case "centerBacks":
      case "fullBacks":
        return pd * 4e-3 + press * 35e-4 - pod * 25e-4 - sp * 25e-4;
      case "mixed":
        return pd * 6e-3 + press * 8e-3 - pod * 2e-3;
      case "targetForward":
        return pd * 0.01 + att.team.tactic.transition.targetForwardUsage * 0.01 + press * 0.012 - pod * 2e-3;
    }
  })();
  const share = Math.max(0.015, Math.min(0.13, baseShare + styleBonus));
  const expected = passCount * share;
  const base = Math.floor(expected);
  const extra = rng.genBool(expected - base) ? 1 : 0;
  return Math.min(passCount, base + extra);
}
function goalkeeperCompletion(att, def) {
  const gk = att.goalkeeper;
  return Math.max(0.48, Math.min(
    0.9,
    0.58 + gk.goalkeeping.distribution * 0.018 + gk.mental.composure * 6e-3 + att.team.tactic.inPossession.playOutOfDefence * 0.025 + att.team.tactic.inPossession.shortPassing * 0.02 - def.team.tactic.outOfPossession.pressingIntensity * 0.09 - def.team.tactic.outOfPossession.preventShortGkDistribution * 0.06
  ));
}
function setPieceTargetBonus(ctx, player) {
  if (ctx.team.tactic.setPieces.aerialTargets.includes(player.id)) return 10;
  return 0;
}
function setPieceExecutionEdge(att, def, shooter, taker) {
  const takerQuality = mean([taker.technical.freeKicks, taker.technical.corners, taker.technical.crossing, taker.mental.vision, taker.mental.decisions]);
  const targetQuality = mean([shooter.technical.heading, shooter.physical.jumpingReach, shooter.physical.strength, shooter.mental.offTheBall]) + setPieceTargetBonus(att, shooter) * 0.35;
  const defendingQuality = avg2(
    def.outfield,
    (p) => mean([p.technical.marking, p.technical.heading, p.physical.jumpingReach, p.mental.concentration])
  ) + def.goalkeeper.goalkeeping.aerialReach * 0.25 + def.goalkeeper.goalkeeping.commandOfArea * 0.25 + def.team.tactic.setPieces.defensiveCorners * 0.18 + def.team.tactic.setPieces.defensiveFreeKicks * 0.12;
  const attackingShape = att.team.tactic.setPieces.attackingCorners * 0.18 + att.team.tactic.setPieces.attackingFreeKicks * 0.12 + att.team.profile.setPieceQuality * 0.1;
  return Math.max(-0.045, Math.min(
    0.075,
    (takerQuality + targetQuality + attackingShape - defendingQuality * 1.6) * 35e-4
  ));
}
function scoreStateModifier(goalDiff) {
  if (goalDiff <= -3) return { shot: 0.045, xg: 0.035 };
  if (goalDiff === -2) return { shot: 0.04, xg: 0.03 };
  if (goalDiff === -1) return { shot: 0.03, xg: 0.022 };
  if (goalDiff === 0) return { shot: 0.015, xg: 8e-3 };
  if (goalDiff === 1) return { shot: -0.01, xg: -6e-3 };
  if (goalDiff === 2) return { shot: -0.02, xg: -0.015 };
  return { shot: -0.03, xg: -0.025 };
}
function runawayModifier(goalDiff) {
  if (goalDiff >= 6) return { shot: -0.18, xg: -0.22, goalCap: 0.12 };
  if (goalDiff === 5) return { shot: -0.14, xg: -0.16, goalCap: 0.18 };
  if (goalDiff === 4) return { shot: -0.1, xg: -0.11, goalCap: 0.25 };
  if (goalDiff === 3) return { shot: -0.065, xg: -0.065, goalCap: 0.35 };
  if (goalDiff === 2) return { shot: -0.035, xg: -0.03, goalCap: 0.48 };
  return { shot: 0, xg: 0, goalCap: 0.72 };
}
function avg2(items, fn, array) {
  const arr = array || items;
  if (arr.length === 0) return 10;
  let sum = 0;
  for (let i = 0; i < arr.length; i++) {
    sum += fn(arr[i], i, arr);
  }
  return sum / arr.length;
}

// src/rng.ts
var Xoshiro128 = class {
  constructor(seed) {
    this.s = new Uint32Array(4);
    let sm = BigInt(seed);
    for (let i = 0; i < 4; i++) {
      sm = sm + 0x9e3779b97f4a7c15n & 0xffffffffffffffffn;
      let z = sm;
      z = (z ^ z >> 30n) * 0xbf58476d1ce4e5b9n & 0xffffffffffffffffn;
      z = (z ^ z >> 27n) * 0x94d049bb133111ebn & 0xffffffffffffffffn;
      z = z ^ z >> 31n;
      this.s[i] = Number(z & 0xffffffffn);
    }
  }
  /** Generate next u32 */
  next() {
    let s0 = this.s[0];
    let s1 = this.s[1];
    let s2 = this.s[2];
    let s3 = this.s[3];
    const result = Math.imul(rotl(s1 * 5, 7), 9);
    const t = s1 << 9;
    s2 ^= s0;
    s3 ^= s1;
    s1 ^= s2;
    s0 ^= s3;
    s2 ^= t;
    s3 = rotl(s3, 11);
    this.s[0] = s0;
    this.s[1] = s1;
    this.s[2] = s2;
    this.s[3] = s3;
    return result >>> 0;
  }
  /** f64 in [0, 1) */
  f64() {
    return this.next() / 4294967296;
  }
  /** Boolean with given probability */
  genBool(prob) {
    return this.f64() < prob;
  }
  /** f64 in [min, max) */
  genRange(min, max) {
    return min + this.f64() * (max - min);
  }
  /** integer in [min, max) */
  genInt(min, max) {
    return min + Math.floor(this.f64() * (max - min));
  }
  /** f64 in [min, max] inclusive */
  genRangeInclusive(min, max) {
    return min + this.f64() * (max - min + 1);
  }
};
function rotl(x, k) {
  return (x << k | x >>> 32 - k) >>> 0;
}
function choosePlayer(players, weightFn, rng) {
  const weights = players.map((p) => Math.max(weightFn(p), 1));
  const total = weights.reduce((a, b) => a + b, 0);
  let r = rng.f64() * total;
  for (let i = 0; i < players.length; i++) {
    r -= weights[i];
    if (r <= 0) return players[i];
  }
  return players[players.length - 1];
}

// src/events.ts
function addPassing(period, att, def, rng) {
  const mismatchMult = Math.max(0.55, Math.min(
    1.28,
    1 + extremeMismatchEdge(att, def) * 0.18 - extremeMismatchEdge(def, att) * 0.28
  ));
  const executionGap = possessionExecutionGap(att);
  const directnessPenalty = Math.max(0, att.team.tactic.inPossession.passingDirectness * 5 - 10) * 0.13;
  const passCount = Math.max(1, Math.round(
    (rng.genRange(2, 7) + att.team.tactic.inPossession.shortPassing * 0.7 + att.control * 0.06 + executionGap * 0.08 - directnessPenalty) * 0.82 * mismatchMult
  ));
  const counterpressDisruption = att.team.tactic.transition.whenPossessionLost === "counterPress" ? -0.03 : 0;
  const directnessCompletionPenalty = Math.max(0, att.team.tactic.inPossession.passingDirectness * 5 - 10) * 0.02;
  const executionCompletionPenalty = executionGap * (0.014 + def.team.tactic.outOfPossession.pressingIntensity * 5e-3);
  const shortExecutionBonus = att.team.tactic.inPossession.shortPassing * Math.max(0, possessionExecutionScore(att) - 12) * 8e-3;
  let completion = 0.75 + att.control * 8e-3 - def.team.tactic.outOfPossession.pressingIntensity * 0.03 + counterpressDisruption - directnessCompletionPenalty - executionCompletionPenalty + shortExecutionBonus + (att.tacticalProfile.positionFit - 0.72) * 0.12 + (att.tacticalProfile.roleFit - 0.7) * 0.08;
  completion = Math.max(0.5, Math.min(0.93, completion));
  const completed = Math.round(passCount * completion);
  const teamStats = period.teamStats[att.teamId];
  teamStats.passesAttempted += passCount;
  teamStats.passesCompleted += completed;
  teamStats.possessionLosses += passCount - completed;
  teamStats.recoveries += rng.genInt(0, 2);
  const gkPasses = goalkeeperPassCount(att, def, passCount, rng);
  const gkCompletion = goalkeeperCompletion(att, def);
  {
    const ps = period.playerStats[att.goalkeeper.id];
    for (let i = 0; i < gkPasses; i++) {
      ps.passesAttempted += 1;
      if (rng.genBool(gkCompletion)) ps.passesCompleted += 1;
    }
  }
  for (let i = gkPasses; i < passCount; i++) {
    const passer = choosePlayer(att.outfield, (p) => passingInvolvementWeight(att, p), rng);
    const ps = period.playerStats[passer.id];
    ps.passesAttempted += 1;
    if (rng.genBool(completion)) ps.passesCompleted += 1;
  }
}
function passingInvolvementWeight(ctx, player) {
  const pos = primaryPosition(player);
  let positionBonus;
  switch (pos) {
    case "CB":
      positionBonus = 24;
      break;
    case "LB":
    case "RB":
    case "LWB":
    case "RWB":
    case "FB":
      positionBonus = 18;
      break;
    case "DM":
      positionBonus = 42;
      break;
    case "CM":
      positionBonus = 38;
      break;
    case "AM":
      positionBonus = 26;
      break;
    case "LW":
    case "RW":
    case "W":
      positionBonus = 10;
      break;
    case "ST":
    case "CF":
      positionBonus = 4;
      break;
    default:
      positionBonus = 16;
  }
  const directness = ctx.team.tactic.inPossession.passingDirectness;
  if (directness >= 3) {
    switch (pos) {
      case "CB":
        positionBonus += 10;
        break;
      case "LB":
      case "RB":
      case "LWB":
      case "RWB":
      case "FB":
        positionBonus += 8;
        break;
      case "DM":
        positionBonus -= 18;
        break;
      case "CM":
        positionBonus -= 14;
        break;
      case "AM":
        positionBonus -= 8;
        break;
      case "ST":
      case "CF":
        positionBonus += 6;
        break;
    }
  } else if (directness <= 1) {
    switch (pos) {
      case "DM":
        positionBonus += 8;
        break;
      case "CM":
        positionBonus += 8;
        break;
      case "AM":
        positionBonus += 5;
        break;
      case "CB":
        positionBonus -= 5;
        break;
      case "ST":
      case "CF":
        positionBonus -= 3;
        break;
    }
  }
  const inst = ctx.instructions.get(player.id);
  const instBonus = inst ? inst.riskyPassing * 0.35 + inst.freedom * 0.15 : 0;
  const slot = ctx.slots[ctx.starters.indexOf(player)] || primaryPosition(player);
  const slotBonus = assignedSlotBonus(slot, "passing");
  return player.technical.passing * 1.45 + player.mental.vision + player.mental.decisions * 0.8 + player.mental.teamwork * 0.45 + positionBonus + instBonus + slotBonus;
}
function addDuels(period, att, def, rng) {
  if (!rng.genBool(0.32)) return;
  const lane = chooseLane(att, def, rng);
  const attackers = laneAttackers(att, lane);
  const defenders = laneDefenders(def, lane);
  const carrierPool = attackers.length > 0 ? attackers : att.outfield;
  const defenderPool = defenders.length > 0 ? defenders : def.outfield;
  const carrier = choosePlayer(carrierPool, (p) => {
    const pos = primaryPosition(p);
    const inst = att.instructions.get(p.id);
    const slot = att.slots[att.starters.indexOf(p)] || primaryPosition(p);
    return p.technical.dribbling + p.physical.acceleration + (isWideOrForward(pos) ? 7 : 1) + (inst ? inst.carryBall * 0.7 : 0) + assignedSlotBonus(slot, "carry");
  }, rng);
  const defender = choosePlayer(
    defenderPool,
    (p) => p.technical.tackling + p.mental.workRate,
    rng
  );
  const laneEdge = laneMatchupEdge(att, def, lane);
  const successProb = Math.max(0.22, Math.min(
    0.74,
    (carrier.technical.dribbling + carrier.physical.agility - defender.technical.tackling - def.team.tactic.outOfPossession.tacklingIntensity * 4.5) * 0.025 + 0.45 + laneEdge * 0.018
  ));
  const success = rng.genBool(successProb);
  const carrierStats = period.playerStats[carrier.id];
  carrierStats.dribblesAttempted += 1;
  carrierStats.groundDuels += 1;
  if (success) {
    carrierStats.dribblesCompleted += 1;
    carrierStats.groundDuelsWon += 1;
    const team = period.teamStats[att.teamId];
    team.dribblesAttempted += 1;
    team.dribblesCompleted += 1;
  } else {
    const defStats = period.playerStats[defender.id];
    defStats.tackles += 1;
    defStats.groundDuels += 1;
    defStats.groundDuelsWon += 1;
    period.teamStats[def.teamId].tackles += 1;
    period.teamStats[att.teamId].dribblesAttempted += 1;
  }
}
function addFoul(state, fouling, victimTeam, half, minute, rng) {
  const eligibleFoulers = fouling.outfield.filter((p) => playerYellowCards(state, p.id) < 2);
  if (eligibleFoulers.length === 0) return;
  const fouler = choosePlayer(eligibleFoulers, (p) => {
    const inst = fouling.instructions.get(p.id);
    return p.mental.aggression + p.technical.tackling + fouling.team.tactic.outOfPossession.tacklingIntensity * 10 + (inst ? inst.pressing * 0.35 : 0);
  }, rng);
  const eligibleVictims = victimTeam.outfield.filter((p) => playerYellowCards(state, p.id) < 2);
  if (eligibleVictims.length === 0) return;
  const victim = choosePlayer(
    eligibleVictims,
    (p) => p.technical.dribbling + p.physical.pace,
    rng
  );
  const yellowProb = Math.max(0.06, Math.min(
    0.35,
    0.17 + fouler.mental.aggression * 5e-3 + fouling.team.tactic.outOfPossession.tacklingIntensity * 0.025 - fouling.team.profile.discipline * 2e-3
  ));
  const previousYellows = playerYellowCards(state, fouler.id);
  const yellow = rng.genBool(previousYellows > 0 ? yellowProb * 0.5 : yellowProb);
  const red = yellow && previousYellows > 0;
  const period = periodForHalf(state, half);
  period.teamStats[fouling.teamId].fouls += 1;
  period.playerStats[fouler.id].foulsCommitted += 1;
  period.playerStats[victim.id].foulsWon += 1;
  if (yellow) {
    period.teamStats[fouling.teamId].yellowCards += 1;
    period.playerStats[fouler.id].yellowCards += 1;
  }
  if (red) {
    period.teamStats[fouling.teamId].redCards += 1;
    period.playerStats[fouler.id].redCards += 1;
  }
  if (yellow) {
    state.timeline.push(makeEvent(
      minute,
      half,
      fouling,
      fouler,
      victim,
      "yellowCard",
      null,
      null,
      state.score,
      `${fouler.name} booked for a challenge on ${victim.name}`
    ));
    if (red) {
      state.timeline.push(makeEvent(
        minute,
        half,
        fouling,
        fouler,
        victim,
        "redCard",
        null,
        null,
        state.score,
        `${fouler.name} receives a second caution`
      ));
    }
  }
}
function addShot(state, att, def, half, minute, rng, xgMultiplier, knockoutDepth) {
  const lane = chooseLane(att, def, rng);
  const laneEdge = laneMatchupEdge(att, def, lane);
  const laneAttackers2 = laneAttackers(att, lane);
  const shooter = choosePlayer(att.outfield, (p) => {
    const inst = att.instructions.get(p.id);
    const slot = att.slots[att.starters.indexOf(p)] || primaryPosition(p);
    return p.technical.finishing * 1.4 + p.technical.longShots * 0.4 + p.mental.offTheBall + setPieceTargetBonus(att, p) + (laneAttackers2.some((lp) => lp.id === p.id) ? 9 : 0) + (inst ? inst.shooting * 0.8 + inst.runs * 0.35 : 0) + assignedSlotBonus(slot, "shoot") + (isForward(primaryPosition(p)) ? 16 : isWideOrForward(primaryPosition(p)) ? 8 : 2);
  }, rng);
  const assister = choosePlayer(att.outfield, (p) => {
    if (p.id === shooter.id) return 1;
    const inst = att.instructions.get(p.id);
    const slot = att.slots[att.starters.indexOf(p)] || primaryPosition(p);
    return p.technical.passing + p.mental.vision + (isMidfielder(primaryPosition(p)) ? 8 : 2) + (inst ? inst.riskyPassing * 0.5 + inst.freedom * 0.25 : 0) + assignedSlotBonus(slot, "assist");
  }, rng);
  const setPiece = rng.genBool(Math.max(0.04, Math.min(0.19, att.team.profile.setPieceQuality * 0.01)));
  const baseXg = setPiece ? rng.genRange(0.04, 0.18) : isForward(primaryPosition(shooter)) ? rng.genRange(0.05, 0.26) : rng.genRange(0.02, 0.12);
  const goalDiff = att.isHome ? state.score.home - state.score.away : state.score.away - state.score.home;
  const scoreState = scoreStateModifier(goalDiff);
  const runaway = runawayModifier(goalDiff);
  let xg = baseXg + (att.shotQuality - def.defence) * 6e-3 + teamStrengthEdge(att, def) * 0.018 + extremeMismatchEdge(att, def) * 0.013 + underdogCounterEdge(att, def) * 0.075 + (setPiece ? setPieceExecutionEdge(att, def, shooter, assister) : 0) + defensiveExposure(def) * 8e-3 + laneEdge * 6e-3 + scoreState.xg + runaway.xg + att.team.tactic.inPossession.workBallIntoBox * 0.01 + att.team.tactic.inPossession.throughBalls * 0.015 + att.team.tactic.inPossession.boxPresence * 0.0125 + Math.max(0, att.team.tactic.base.risk - 3) * 6e-3 - att.team.tactic.inPossession.shootOnSight * 6e-3 + tacticalMatchupEffect(att, def).xgModifier + (att.instructions.get(shooter.id)?.runs || 0) * 2e-3 + assignedSlotBonus(att.slots[att.starters.indexOf(shooter)] || primaryPosition(shooter), "xg") * 0.01 - def.team.tactic.outOfPossession.compactness * 0.02;
  xg = Math.max(0.03, Math.min(0.62, xg * (xgMultiplier ?? 1)));
  const shotOnTargetProb = Math.max(0.28, Math.min(
    0.69,
    0.34 + shooter.technical.finishing * 0.016 + shooter.mental.composure * 55e-4 + xg * 0.39
  ));
  const onTarget = rng.genBool(shotOnTargetProb);
  let goal = false;
  let goalProbability = 0;
  if (onTarget) {
    const koConversionCap = knockoutDepth ? Math.max(1.1, 1.35 - knockoutDepth * 0.03) : 1.4;
    const conversionEdge = Math.max(0.78, Math.min(
      koConversionCap,
      1 + teamStrengthEdge(att, def) * 0.1 + extremeMismatchEdge(att, def) * 0.08 + underdogCounterEdge(att, def) * 0.82 + competitiveConversionEdge(att, def) + favoriteConversionEdge(att, def)
    ));
    goalProbability = Math.max(0.022, Math.min(
      runaway.goalCap,
      xg * (0.98 + shooter.technical.finishing * 0.021 + shooter.mental.composure * 0.014) / (0.78 + goalkeeperScore(def.goalkeeper) * 0.025) * conversionEdge
    ));
    goal = rng.genBool(goalProbability);
  }
  const crossAttempt = rng.genBool(Math.max(0.1, Math.min(0.32, 0.18 + att.team.tactic.inPossession.crossingFrequency * 0.03)));
  const aerialDuel = setPiece || crossAttempt;
  let aerialWon = false;
  if (aerialDuel) {
    const aerialDefenders = laneDefenders(def, lane);
    const aerialDefenderPool = aerialDefenders.length > 0 ? aerialDefenders : def.outfield;
    const aerialDefender = choosePlayer(aerialDefenderPool, (p) => p.technical.heading + p.physical.jumpingReach + p.technical.marking + p.mental.concentration, rng);
    const attackAerial = shooter.technical.heading + shooter.physical.jumpingReach + shooter.physical.strength * 0.45 + setPieceTargetBonus(att, shooter) * 0.5;
    const defenceAerial = aerialDefender.technical.heading + aerialDefender.physical.jumpingReach + aerialDefender.technical.marking * 0.5 + def.goalkeeper.goalkeeping.aerialReach * 0.35 + def.goalkeeper.goalkeeping.commandOfArea * 0.25;
    aerialWon = rng.genBool(Math.max(0.24, Math.min(0.74, (attackAerial - defenceAerial) * 0.018 + 0.48)));
  }
  if (goal) {
    if (att.isHome) state.score.home += 1;
    else state.score.away += 1;
  }
  const period = periodForHalf(state, half);
  const team = period.teamStats[att.teamId];
  team.shots += 1;
  team.xg = round2(team.xg + xg);
  if (setPiece) team.setPieceXg = round2(team.setPieceXg + xg);
  if (onTarget) team.shotsOnTarget += 1;
  if (crossAttempt) {
    team.corners += 1;
    team.crosses += 1;
  }
  const shooterStats = period.playerStats[shooter.id];
  shooterStats.shots += 1;
  shooterStats.xg = round2(shooterStats.xg + xg);
  if (onTarget) shooterStats.shotsOnTarget += 1;
  if (aerialDuel) {
    shooterStats.aerialDuels += 1;
    if (aerialWon) shooterStats.aerialDuelsWon += 1;
  }
  const assisterStats = period.playerStats[assister.id];
  assisterStats.keyPasses += 1;
  assisterStats.xa = round2(assisterStats.xa + xg * 0.85);
  if (crossAttempt) assisterStats.crosses += 1;
  team.keyPasses += 1;
  team.xa = round2(team.xa + xg * 0.85);
  if (onTarget && !goal) {
    period.teamStats[def.teamId].saves += 1;
    period.playerStats[def.goalkeeper.id].saves += 1;
  }
  if (goal) {
    period.teamStats[att.teamId].goals += 1;
    period.playerStats[shooter.id].goals += 1;
    period.playerStats[assister.id].assists += 1;
    period.playerStats[def.goalkeeper.id].goalsConceded += 1;
  }
  if (goal) {
    state.timeline.push(makeEvent(
      minute,
      half,
      att,
      shooter,
      assister,
      "goal",
      round2(xg),
      round2(xg * 0.85),
      state.score,
      `${shooter.name} scores, assisted by ${assister.name}`
    ));
  } else if (xg >= 0.28) {
    state.timeline.push(makeEvent(
      minute,
      half,
      att,
      shooter,
      assister,
      "bigChance",
      round2(xg),
      round2(xg * 0.85),
      state.score,
      `${shooter.name} has a big chance`
    ));
  }
}
function addDefensiveAction(period, def, rng) {
  const defender = choosePlayer(def.outfield, (p) => p.technical.marking + p.technical.tackling + p.mental.anticipation, rng);
  const stats = period.playerStats[defender.id];
  const action = rng.genInt(0, 3);
  if (action === 0) {
    stats.interceptions += 1;
    period.teamStats[def.teamId].interceptions += 1;
  } else if (action === 1) {
    stats.clearances += 1;
    period.teamStats[def.teamId].clearances += 1;
  } else {
    stats.blocks += 1;
    period.teamStats[def.teamId].blocks += 1;
  }
}
function playerYellowCards(state, playerId) {
  return (state.firstHalf.playerStats[playerId]?.yellowCards || 0) + (state.secondHalf.playerStats[playerId]?.yellowCards || 0);
}
function periodForHalf(state, half) {
  return half === "first" ? state.firstHalf : state.secondHalf;
}
function makeEvent(minute, half, ctx, player, related, eventType, xg, xa, scoreAfter, description) {
  return {
    minute,
    stoppageTime: minute === 45 || minute === 90 ? 1 : null,
    half,
    teamId: ctx.teamId,
    playerId: player?.id || null,
    relatedPlayerId: related?.id || null,
    eventType,
    xg,
    xa,
    scoreAfter: { ...scoreAfter },
    description
  };
}

// src/substitutions.ts
function applySubstitutions(state, ctx, rng) {
  let count = substitutionCount(state, ctx, rng);
  const injury = forcedInjurySubstitution(ctx, rng);
  if (injury) {
    count = Math.max(1, Math.min(5, count + 1));
  }
  const firstMinute = substitutionFirstMinute(state, ctx, injury ? injury.minute : null, rng);
  const substitutes = selectSubstitutes(ctx, count);
  const usedOutgoing = [];
  let injuryRecorded = false;
  for (let idx = 0; idx < substitutes.length; idx++) {
    const substitute = substitutes[idx];
    const substitutePosition = primaryPosition(substitute);
    let outgoing = null;
    if (!injuryRecorded && injury) {
      if (!usedOutgoing.includes(injury.player.id)) {
        injuryRecorded = true;
        outgoing = injury.player;
      }
    }
    if (!outgoing) {
      const candidates = ctx.starters.filter((p) => primaryPosition(p) !== "GK").filter((p) => !usedOutgoing.includes(p.id));
      if (candidates.length > 0) {
        candidates.sort((a, b) => substitutionMatchScore(substitutePosition, primaryPosition(b)) - substitutionMatchScore(substitutePosition, primaryPosition(a)));
        outgoing = candidates[0];
      }
    }
    if (!outgoing) continue;
    usedOutgoing.push(outgoing.id);
    const minute = Math.min(88, firstMinute + idx * rng.genInt(4, 8));
    const period = state.secondHalf;
    period.playerStats[outgoing.id].minutes = Math.max(0, minute - 46);
    period.playerStats[substitute.id].minutes = 91 - minute;
    transferSubstituteStats(period, outgoing.id, substitute.id, minute, rng);
    if (injuryRecorded && idx === 0) {
      state.timeline.push(makeSubEvent(
        minute,
        ctx,
        outgoing,
        null,
        "injury",
        state.score,
        `${outgoing.name} is forced off after an injury concern`
      ));
    }
    state.timeline.push(makeSubEvent(
      minute,
      ctx,
      substitute,
      outgoing,
      "substitution",
      state.score,
      `${substitute.name} replaces ${outgoing.name}`
    ));
  }
}
function substitutionCount(state, ctx, rng) {
  let count = 3;
  const avgCondition = avg(ctx.starters, (p) => p.status.condition);
  const fatiguePressure = ctx.team.profile.fatigue + (100 - avgCondition) * 0.65;
  if (isLosing(state, ctx)) count += 1;
  if (fatiguePressure > 38 || ctx.team.profile.injuryPressure > 13) count += 1;
  if (isWinning(state, ctx) && fatiguePressure < 20 && rng.genBool(0.35)) count -= 1;
  if (rng.genBool(0.16)) count += 1;
  if (rng.genBool(0.18)) count -= 1;
  return Math.max(1, Math.min(5, count));
}
function forcedInjurySubstitution(ctx, rng) {
  const injuryProb = Math.max(0.02, Math.min(0.22, ctx.team.profile.injuryPressure * 6e-3 + ctx.team.profile.fatigue * 3e-3));
  if (!rng.genBool(injuryProb)) return null;
  const injured = choosePlayer(ctx.outfield, (p) => p.status.injuryRisk * 1.6 + (100 - p.status.condition), rng);
  return { player: injured, minute: rng.genInt(48, 78) };
}
function substitutionFirstMinute(state, ctx, injuryMinute, rng) {
  if (injuryMinute !== null) return Math.max(48, Math.min(82, injuryMinute));
  const avgCondition = avg(ctx.starters, (p) => p.status.condition);
  const latestGoal = latestGoalMinute(state);
  let minute;
  if (isLosing(state, ctx)) {
    minute = latestGoal !== null ? latestGoal + 5 : 58;
  } else if (isWinning(state, ctx)) {
    minute = latestGoal !== null ? latestGoal + 7 : 68;
  } else if (avgCondition < 74 || ctx.team.profile.fatigue > 18) {
    minute = 56;
  } else {
    minute = 63;
  }
  minute += rng.genInt(0, 8);
  return Math.max(52, Math.min(78, minute));
}
function latestGoalMinute(state) {
  let latest = null;
  for (const event of state.timeline) {
    if (event.eventType === "goal" && (latest === null || event.minute > latest)) {
      latest = event.minute;
    }
  }
  return latest;
}
function isLosing(state, ctx) {
  return ctx.isHome ? state.score.home < state.score.away : state.score.away < state.score.home;
}
function isWinning(state, ctx) {
  return ctx.isHome ? state.score.home > state.score.away : state.score.away > state.score.home;
}
function selectSubstitutes(ctx, count) {
  const candidates = ctx.bench.filter((p) => primaryPosition(p) !== "GK").sort((a, b) => b.hidden.currentAbility - a.hidden.currentAbility);
  const selected = [];
  const usedGroups = [];
  for (const candidate of candidates) {
    const group = positionGroup(primaryPosition(candidate));
    if (usedGroups.includes(group)) continue;
    selected.push(candidate);
    usedGroups.push(group);
    if (selected.length >= count) return selected;
  }
  for (const candidate of candidates) {
    if (selected.some((p) => p.id === candidate.id)) continue;
    selected.push(candidate);
    if (selected.length >= count) break;
  }
  return selected;
}
function substitutionMatchScore(substitutePosition, outgoingPosition) {
  if (substitutePosition === outgoingPosition) return 100;
  if (positionGroup(substitutePosition) === positionGroup(outgoingPosition)) return 70;
  if (compatibleSubstitution(substitutePosition, outgoingPosition)) return 45;
  return 5;
}
function compatibleSubstitution(a, b) {
  const pairs = [
    ["CB", "DM"],
    ["DM", "CB"],
    ["CM", "LW"],
    ["CM", "RW"],
    ["AM", "LW"],
    ["AM", "RW"],
    ["LW", "ST"],
    ["RW", "ST"],
    ["ST", "LW"],
    ["ST", "RW"]
  ];
  return pairs.some(([x, y]) => x === a && y === b);
}
function transferSubstituteStats(period, outgoingId, substituteId, minute, rng) {
  const substituteShare = Math.max(0, Math.min(0.55, (91 - minute) / 45)) * rng.genRange(0.65, 1.15);
  if (substituteShare <= 0.02) return;
  const outgoing = period.playerStats[outgoingId];
  if (!outgoing) return;
  const moves = {
    passesAttempted: { attempted: scaledU16(outgoing.passesAttempted, substituteShare, rng), completed: 0 },
    keyPasses: { attempted: scaledU16(outgoing.keyPasses, substituteShare * 0.8, rng), completed: 0 },
    crosses: { attempted: scaledU16(outgoing.crosses, substituteShare, rng), completed: 0 },
    dribblesAttempted: { attempted: scaledU16(outgoing.dribblesAttempted, substituteShare, rng), completed: 0 },
    tackles: { attempted: scaledU16(outgoing.tackles, substituteShare, rng), completed: 0 },
    interceptions: { attempted: scaledU16(outgoing.interceptions, substituteShare, rng), completed: 0 },
    clearances: { attempted: scaledU16(outgoing.clearances, substituteShare, rng), completed: 0 },
    blocks: { attempted: scaledU16(outgoing.blocks, substituteShare, rng), completed: 0 },
    aerialDuels: { attempted: scaledU16(outgoing.aerialDuels, substituteShare, rng), completed: 0 },
    groundDuels: { attempted: scaledU16(outgoing.groundDuels, substituteShare, rng), completed: 0 },
    foulsCommitted: { attempted: scaledU16(outgoing.foulsCommitted, substituteShare * 0.7, rng), completed: 0 },
    foulsWon: { attempted: scaledU16(outgoing.foulsWon, substituteShare * 0.7, rng), completed: 0 }
  };
  const passCompletion = ratio(outgoing.passesCompleted, outgoing.passesAttempted);
  const dribbleCompletion = ratio(outgoing.dribblesCompleted, outgoing.dribblesAttempted);
  const aerialWinRate = ratio(outgoing.aerialDuelsWon, outgoing.aerialDuels);
  const groundWinRate = ratio(outgoing.groundDuelsWon, outgoing.groundDuels);
  const scalarFields = [
    "passesAttempted",
    "keyPasses",
    "crosses",
    "dribblesAttempted",
    "tackles",
    "interceptions",
    "clearances",
    "blocks",
    "aerialDuels",
    "groundDuels",
    "foulsCommitted",
    "foulsWon"
  ];
  for (const key of scalarFields) {
    moves[key].completed = 0;
  }
  const completedValues = {
    passesCompleted: cappedScaled(moves.passesAttempted.attempted, passCompletion, rng),
    dribblesCompleted: cappedScaled(moves.dribblesAttempted.attempted, dribbleCompletion, rng),
    aerialDuelsWon: cappedScaled(moves.aerialDuels.attempted, aerialWinRate, rng),
    groundDuelsWon: cappedScaled(moves.groundDuels.attempted, groundWinRate, rng)
  };
  subtractFromStat(outgoing, "passesAttempted", moves.passesAttempted.attempted);
  subtractFromStat(outgoing, "passesCompleted", completedValues.passesCompleted);
  subtractFromStat(outgoing, "keyPasses", moves.keyPasses.attempted);
  subtractFromStat(outgoing, "crosses", moves.crosses.attempted);
  subtractFromStat(outgoing, "dribblesAttempted", moves.dribblesAttempted.attempted);
  subtractFromStat(outgoing, "dribblesCompleted", completedValues.dribblesCompleted);
  subtractFromStat(outgoing, "tackles", moves.tackles.attempted);
  subtractFromStat(outgoing, "interceptions", moves.interceptions.attempted);
  subtractFromStat(outgoing, "clearances", moves.clearances.attempted);
  subtractFromStat(outgoing, "blocks", moves.blocks.attempted);
  subtractFromStat(outgoing, "aerialDuels", moves.aerialDuels.attempted);
  subtractFromStat(outgoing, "aerialDuelsWon", completedValues.aerialDuelsWon);
  subtractFromStat(outgoing, "groundDuels", moves.groundDuels.attempted);
  subtractFromStat(outgoing, "groundDuelsWon", completedValues.groundDuelsWon);
  subtractFromStat(outgoing, "foulsCommitted", moves.foulsCommitted.attempted);
  subtractFromStat(outgoing, "foulsWon", moves.foulsWon.attempted);
  const sub = period.playerStats[substituteId];
  if (!sub) return;
  addToStat(sub, "passesAttempted", moves.passesAttempted.attempted);
  addToStat(sub, "passesCompleted", completedValues.passesCompleted);
  addToStat(sub, "keyPasses", moves.keyPasses.attempted);
  addToStat(sub, "crosses", moves.crosses.attempted);
  addToStat(sub, "dribblesAttempted", moves.dribblesAttempted.attempted);
  addToStat(sub, "dribblesCompleted", completedValues.dribblesCompleted);
  addToStat(sub, "tackles", moves.tackles.attempted);
  addToStat(sub, "interceptions", moves.interceptions.attempted);
  addToStat(sub, "clearances", moves.clearances.attempted);
  addToStat(sub, "blocks", moves.blocks.attempted);
  addToStat(sub, "aerialDuels", moves.aerialDuels.attempted);
  addToStat(sub, "aerialDuelsWon", completedValues.aerialDuelsWon);
  addToStat(sub, "groundDuels", moves.groundDuels.attempted);
  addToStat(sub, "groundDuelsWon", completedValues.groundDuelsWon);
  addToStat(sub, "foulsCommitted", moves.foulsCommitted.attempted);
  addToStat(sub, "foulsWon", moves.foulsWon.attempted);
}
function scaledU16(value, share, rng) {
  if (value === 0) return 0;
  const expected = Math.max(0, Math.min(value, value * share));
  const base = Math.floor(expected);
  const extra = rng.genBool(expected - base) ? 1 : 0;
  return Math.min(value, base + extra);
}
function cappedScaled(total, ratioVal, rng) {
  return scaledU16(total, Math.max(0, Math.min(1, ratioVal)), rng);
}
function ratio(numerator, denominator) {
  if (denominator === 0) return 0;
  return numerator / denominator;
}
function subtractFromStat(stats, key, value) {
  stats[key] = Math.max(0, stats[key] - value);
}
function addToStat(stats, key, value) {
  stats[key] = (stats[key] || 0) + value;
}
function makeSubEvent(minute, ctx, player, related, eventType, scoreAfter, description) {
  return {
    minute,
    stoppageTime: null,
    half: "second",
    teamId: ctx.teamId,
    playerId: player?.id || null,
    relatedPlayerId: related?.id || null,
    eventType,
    xg: null,
    xa: null,
    scoreAfter: { ...scoreAfter },
    description
  };
}

// src/ratings.ts
function finalizeRatings(period) {
  for (const playerId of Object.keys(period.playerStats)) {
    const player = period.playerStats[playerId];
    if (!shouldRatePlayer(player)) {
      player.rating = null;
      continue;
    }
    let rating = 6;
    rating += player.goals * 1.6;
    rating += player.assists * 1.05;
    rating += Math.min(player.xg, 1.5) * 0.55;
    rating += Math.min(player.xa, 1.5) * 0.4;
    rating += player.shotsOnTarget * 0.18;
    rating += player.keyPasses * 0.16;
    rating += player.dribblesCompleted * 0.12;
    rating += player.tackles * 0.13;
    rating += player.interceptions * 0.14;
    rating += player.clearances * 0.04;
    rating += player.blocks * 0.12;
    rating += player.saves * 0.22;
    rating += player.foulsWon * 0.04;
    rating -= Math.max(0, player.shots - player.shotsOnTarget) * 0.09;
    rating -= player.foulsCommitted * 0.12;
    rating -= player.yellowCards * 0.5;
    rating -= player.redCards * 2.4;
    rating -= player.errors * 1.2;
    rating -= player.goalsConceded * (player.position === "GK" ? 0.3 : 0.07);
    rating += passRatingDelta(player);
    rating += duelRatingDelta(player);
    rating += minutesRatingAdjustment(player.minutes);
    player.rating = round2(Math.max(5, Math.min(10, rating)));
    player.xg = round2(player.xg);
    player.xa = round2(player.xa);
  }
  for (const teamId of Object.keys(period.teamStats)) {
    const team = period.teamStats[teamId];
    team.xg = round2(team.xg);
    team.xa = round2(team.xa);
    team.setPieceXg = round2(team.setPieceXg);
  }
}
function shouldRatePlayer(player) {
  if (player.minutes === 0) return false;
  if (player.minutes >= 20) return true;
  return hasKeyPositiveAction(player) || hasKeyNegativeAction(player);
}
function hasKeyPositiveAction(player) {
  return player.goals > 0 || player.assists > 0 || player.xg >= 0.35 || player.xa >= 0.35 || player.saves >= 4;
}
function hasKeyNegativeAction(player) {
  return player.redCards > 0 || player.yellowCards > 0 || player.errors > 0 || player.foulsCommitted >= 3 || player.position === "GK" && player.goalsConceded >= 2;
}
function passRatingDelta(player) {
  if (player.passesAttempted < 10) return 0;
  const completion = player.passesCompleted / player.passesAttempted;
  const volume = Math.max(0.2, Math.min(1, player.passesAttempted / 80));
  return (completion - 0.78) * 2.2 * volume;
}
function duelRatingDelta(player) {
  const total = player.groundDuels + player.aerialDuels;
  const won = player.groundDuelsWon + player.aerialDuelsWon;
  if (total < 3) return 0;
  const winRate = won / total;
  return (winRate - 0.5) * 1.1;
}
function minutesRatingAdjustment(minutes) {
  if (minutes < 20) return -0.1;
  if (minutes >= 75) return 0.05;
  return 0;
}

// src/merge-periods.ts
function mergePeriods(first, second) {
  const full = {
    teamStats: {},
    playerStats: {}
  };
  for (const teamId of Object.keys(first.teamStats)) {
    full.teamStats[teamId] = { ...first.teamStats[teamId] };
  }
  for (const playerId of Object.keys(first.playerStats)) {
    full.playerStats[playerId] = { ...first.playerStats[playerId] };
  }
  for (const teamId of Object.keys(second.teamStats)) {
    if (!full.teamStats[teamId]) {
      full.teamStats[teamId] = { ...second.teamStats[teamId] };
      continue;
    }
    const entry = full.teamStats[teamId];
    const s = second.teamStats[teamId];
    entry.goals += s.goals;
    entry.xg = round2(entry.xg + s.xg);
    entry.xa = round2(entry.xa + s.xa);
    entry.shots += s.shots;
    entry.shotsOnTarget += s.shotsOnTarget;
    entry.passesAttempted += s.passesAttempted;
    entry.passesCompleted += s.passesCompleted;
    entry.keyPasses += s.keyPasses;
    entry.crosses += s.crosses;
    entry.dribblesAttempted += s.dribblesAttempted;
    entry.dribblesCompleted += s.dribblesCompleted;
    entry.tackles += s.tackles;
    entry.interceptions += s.interceptions;
    entry.clearances += s.clearances;
    entry.blocks += s.blocks;
    entry.saves += s.saves;
    entry.fouls += s.fouls;
    entry.yellowCards += s.yellowCards;
    entry.redCards += s.redCards;
    entry.corners += s.corners;
    entry.offsides += s.offsides;
    entry.setPieceXg = round2(entry.setPieceXg + s.setPieceXg);
    entry.possessionLosses += s.possessionLosses;
    entry.recoveries += s.recoveries;
    entry.possession = round2((entry.possession + s.possession) / 2);
  }
  for (const playerId of Object.keys(second.playerStats)) {
    if (!full.playerStats[playerId]) {
      full.playerStats[playerId] = { ...second.playerStats[playerId] };
      continue;
    }
    const entry = full.playerStats[playerId];
    const s = second.playerStats[playerId];
    entry.minutes += s.minutes;
    entry.goals += s.goals;
    entry.assists += s.assists;
    entry.xg = round2(entry.xg + s.xg);
    entry.xa = round2(entry.xa + s.xa);
    entry.shots += s.shots;
    entry.shotsOnTarget += s.shotsOnTarget;
    entry.passesAttempted += s.passesAttempted;
    entry.passesCompleted += s.passesCompleted;
    entry.keyPasses += s.keyPasses;
    entry.crosses += s.crosses;
    entry.dribblesAttempted += s.dribblesAttempted;
    entry.dribblesCompleted += s.dribblesCompleted;
    entry.tackles += s.tackles;
    entry.interceptions += s.interceptions;
    entry.clearances += s.clearances;
    entry.blocks += s.blocks;
    entry.aerialDuels += s.aerialDuels;
    entry.aerialDuelsWon += s.aerialDuelsWon;
    entry.groundDuels += s.groundDuels;
    entry.groundDuelsWon += s.groundDuelsWon;
    entry.foulsCommitted += s.foulsCommitted;
    entry.foulsWon += s.foulsWon;
    entry.yellowCards += s.yellowCards;
    entry.redCards += s.redCards;
    entry.saves += s.saves;
    entry.goalsConceded += s.goalsConceded;
    entry.errors += s.errors;
  }
  finalizeRatings(full);
  return full;
}

// src/simulate.ts
var ENGINE_VERSION = "0.1.0";
function simulateMatch(home, away, seed, knockoutDepth) {
  const rng = new Xoshiro128(seed);
  const homeInflated = inflateTeam(home);
  const awayInflated = inflateTeam(away);
  const homeCtx = buildTeamContext(
    home.id,
    home.name,
    home.tactic,
    homeInflated.profile,
    homeInflated.coach,
    homeInflated.players,
    homeInflated.startingXi,
    homeInflated.bench,
    true
  );
  const awayCtx = buildTeamContext(
    away.id,
    away.name,
    away.tactic,
    awayInflated.profile,
    awayInflated.coach,
    awayInflated.players,
    awayInflated.startingXi,
    awayInflated.bench,
    false
  );
  validateTeam(home, homeInflated);
  validateTeam(away, awayInflated);
  validateTeam(home, homeInflated);
  validateTeam(away, awayInflated);
  const state = {
    score: { home: 0, away: 0 },
    firstHalf: emptyPeriod(home, away),
    secondHalf: emptyPeriod(home, away),
    timeline: []
  };
  seedLineups(state.firstHalf, homeCtx, awayCtx, 45);
  seedLineups(state.secondHalf, homeCtx, awayCtx, 45);
  simulateHalf(state, homeCtx, awayCtx, "first", rng, knockoutDepth);
  simulateHalf(state, homeCtx, awayCtx, "second", rng, knockoutDepth);
  applySubstitutions(state, homeCtx, rng);
  applySubstitutions(state, awayCtx, rng);
  finalizeRatings(state.firstHalf);
  finalizeRatings(state.secondHalf);
  const full = mergePeriods(state.firstHalf, state.secondHalf);
  const score = {
    home: full.teamStats[home.id]?.goals || 0,
    away: full.teamStats[away.id]?.goals || 0
  };
  state.timeline.sort((a, b) => {
    const halfA = a.half === "first" ? 0 : 1;
    const halfB = b.half === "first" ? 0 : 1;
    if (halfA !== halfB) return halfA - halfB;
    if (a.minute !== b.minute) return a.minute - b.minute;
    return (a.stoppageTime || 0) - (b.stoppageTime || 0);
  });
  return {
    seed,
    metadata: {
      homeTeamId: home.id,
      homeTeamName: home.name,
      awayTeamId: away.id,
      awayTeamName: away.name,
      engineVersion: ENGINE_VERSION,
      simulationModel: "event-level statistical simulator"
    },
    teams: {
      home: teamSnapshot(home, homeInflated, homeCtx),
      away: teamSnapshot(away, awayInflated, awayCtx)
    },
    score,
    periods: {
      fullMatch: full,
      firstHalf: state.firstHalf,
      secondHalf: state.secondHalf
    },
    timeline: state.timeline
  };
}
function teamSnapshot(team, inflated, ctx) {
  return {
    id: team.id,
    name: team.name,
    coach: inflated.coach,
    profile: inflated.profile,
    tactic: team.tactic,
    lineup: ctx.starters.map((player, idx) => {
      const slot = ctx.slots[idx] || primaryPosition(player);
      const instruction = ctx.instructions.get(player.id);
      return {
        playerId: player.id,
        name: player.name,
        slot,
        naturalPosition: primaryPosition(player),
        role: instruction?.role || null,
        duty: instruction?.duty || null,
        positionFit: round2(positionFit(player, slot))
      };
    })
  };
}
function validateTeam(team, inflated) {
  if (inflated.startingXi.length === 0) {
    throw new SimulationError("emptyStartingXi", `team ${team.id} has an empty starting XI`);
  }
  const ids = new Set(inflated.players.map((p) => p.id));
  for (const playerId of [...inflated.startingXi, ...inflated.bench]) {
    if (!ids.has(playerId)) {
      throw new SimulationError("missingPlayer", `team ${team.id} references missing player ${playerId}`);
    }
  }
}
function seedLineups(period, home, away, minutes) {
  for (const ctx of [home, away]) {
    for (const player of ctx.starters) {
      const stats = period.playerStats[player.id];
      if (stats) stats.minutes = minutes;
    }
  }
}
function emptyPeriod(home, away) {
  const playerStats = {};
  for (const team of [home, away]) {
    for (const player of team.players) {
      playerStats[player.id] = {
        playerId: player.id,
        teamId: team.id,
        name: player.name,
        position: player.position,
        minutes: 0,
        goals: 0,
        assists: 0,
        xg: 0,
        xa: 0,
        shots: 0,
        shotsOnTarget: 0,
        passesAttempted: 0,
        passesCompleted: 0,
        keyPasses: 0,
        crosses: 0,
        dribblesAttempted: 0,
        dribblesCompleted: 0,
        tackles: 0,
        interceptions: 0,
        clearances: 0,
        blocks: 0,
        aerialDuels: 0,
        aerialDuelsWon: 0,
        groundDuels: 0,
        groundDuelsWon: 0,
        foulsCommitted: 0,
        foulsWon: 0,
        yellowCards: 0,
        redCards: 0,
        saves: 0,
        goalsConceded: 0,
        errors: 0,
        rating: null
      };
    }
  }
  const teamStats = {};
  teamStats[home.id] = createEmptyTeamStats();
  teamStats[away.id] = createEmptyTeamStats();
  return { teamStats, playerStats };
}
function createEmptyTeamStats() {
  return {
    goals: 0,
    xg: 0,
    xa: 0,
    possession: 0,
    shots: 0,
    shotsOnTarget: 0,
    passesAttempted: 0,
    passesCompleted: 0,
    keyPasses: 0,
    crosses: 0,
    dribblesAttempted: 0,
    dribblesCompleted: 0,
    tackles: 0,
    interceptions: 0,
    clearances: 0,
    blocks: 0,
    saves: 0,
    fouls: 0,
    yellowCards: 0,
    redCards: 0,
    corners: 0,
    offsides: 0,
    setPieceXg: 0,
    possessionLosses: 0,
    recoveries: 0
  };
}
function simulateHalf(state, home, away, half, rng, knockoutDepth) {
  const possessionHome = possessionProbability(home, away);
  const tempoSum = (home.team.tactic.base.tempo + away.team.tactic.base.tempo) * 5;
  const possessions = Math.max(48, Math.min(74, Math.round(tempoSum * 1.32 + 31)));
  const minutesStart = half === "first" ? 1 : 46;
  for (let step = 0; step < possessions; step++) {
    const minute = Math.min(90, minutesStart + Math.floor(step / possessions * 45));
    const attackingHome = rng.genBool(possessionHome);
    const [att, def] = attackingHome ? [home, away] : [away, home];
    simulatePossession(state, att, def, half, minute, rng, knockoutDepth);
  }
  applyPeriodPossession(periodForHalf2(state, half), home, away, possessionHome, half);
}
function simulatePossession(state, att, def, half, minute, rng, knockoutDepth) {
  const attackEdge = att.attack - def.defence;
  const strengthEdge = teamStrengthEdge(att, def);
  const mismatchEdge = extremeMismatchEdge(att, def);
  const counterEdge = underdogCounterEdge(att, def);
  const defExposure = defensiveExposure(def);
  const attExecutionGap = possessionExecutionGap(att);
  const tactMatchup = tacticalMatchupEffect(att, def);
  const tacticEdge = att.team.tactic.inPossession.throughBalls * 0.015 + att.team.tactic.transition.quickSwitches * 0.01 + (att.tacticalProfile.positionFit - 0.72) * 0.06 + (att.tacticalProfile.roleFit - 0.7) * 0.04 + (att.tacticalProfile.tacticFit - 12) * 6e-3 - def.team.tactic.outOfPossession.compactness * 0.02 + tactMatchup.shotVolumeModifier;
  const mentalityFactor = 0.22 + att.team.tactic.base.risk * 0.163;
  const shootOnSightBonus = att.team.tactic.inPossession.shootOnSight * 4e-3 + att.team.tactic.inPossession.crossingFrequency * 25e-4;
  const regroupDefence = att.team.tactic.transition.whenPossessionLost === "regroup" ? -0.012 : 0;
  const counterpressGap = att.team.tactic.transition.whenPossessionLost === "counterPress" ? 0.015 : 0;
  const goalDiff = att.isHome ? state.score.home - state.score.away : state.score.away - state.score.home;
  const scoreState = scoreStateModifier(goalDiff);
  const runaway = runawayModifier(goalDiff);
  const shotFactor = knockoutDepth ? Math.max(0.35, 0.78 - (knockoutDepth - 1) * 0.03) : 1;
  const xgFactor = knockoutDepth ? Math.max(0.45, 0.85 - (knockoutDepth - 1) * 0.02) : 1;
  const shotProbability = Math.max(0.04, Math.min(
    0.36,
    (0.07 + attackEdge * 4e-3 + strengthEdge * 0.025 + mismatchEdge * 8e-3 + counterEdge * 0.041 + defExposure * 6e-3 - attExecutionGap * 8e-3 + tacticEdge + shootOnSightBonus + regroupDefence + counterpressGap + tactMatchup.xgModifier * 0.55 + scoreState.shot + runaway.shot) * mentalityFactor + att.team.tactic.base.risk * 6e-3 + Math.max(0, att.team.tactic.base.risk - 3) * 0.024 + att.team.tactic.inPossession.boxPresence * 4e-3
  )) * shotFactor;
  const foulProbability = Math.max(0.03, Math.min(
    0.26,
    0.08 + def.team.tactic.outOfPossession.tacklingIntensity * 0.035 + def.team.tactic.outOfPossession.pressingIntensity * 0.025 + tactMatchup.foulCardModifier - def.discipline * 8e-4
  ));
  const xgMultiplier = xgFactor;
  {
    const period = periodForHalf2(state, half);
    addPassing(period, att, def, rng);
    addDuels(period, att, def, rng);
  }
  if (rng.genBool(dangerousTurnoverProbability(att, def))) {
    addShot(state, def, att, half, minute, rng, xgMultiplier, knockoutDepth);
    return;
  }
  if (rng.genBool(foulProbability)) {
    addFoul(state, def, att, half, minute, rng);
    return;
  }
  if (rng.genBool(shotProbability)) {
    addShot(state, att, def, half, minute, rng, xgMultiplier, knockoutDepth);
  } else if (rng.genBool(0.08)) {
    const period = periodForHalf2(state, half);
    addDefensiveAction(period, def, rng);
  }
}
function applyPeriodPossession(period, home, away, homePossessionProb, half) {
  const halfAdjustment = half === "first" ? 0 : 0.015;
  const homePossession = Math.round(Math.max(0.15, Math.min(0.88, homePossessionProb + halfAdjustment)) * 1e3) / 10;
  if (period.teamStats[home.teamId]) period.teamStats[home.teamId].possession = homePossession;
  if (period.teamStats[away.teamId]) period.teamStats[away.teamId].possession = 100 - homePossession;
}
function periodForHalf2(state, half) {
  return half === "first" ? state.firstHalf : state.secondHalf;
}
export {
  SimulationError,
  Xoshiro128,
  addDefensiveAction,
  addDuels,
  addFoul,
  addPassing,
  addShot,
  adjacentPosition,
  avg,
  buildTeamContext,
  choosePlayer,
  finalizeRatings,
  formationShape,
  formationSlots,
  inflatePlayer,
  inflateTeam,
  isWidePosition,
  mean,
  mergePeriods,
  positionFit,
  positionGroup,
  primaryPosition,
  round2,
  samePositionGroup,
  simulateMatch,
  syntheticPlayer
};
//# sourceMappingURL=index.js.map